import { z } from "zod";
import { BankDetails } from "../catalog/bank-account.routes.js";
import { Id } from "../common/ids.js";
import { Money } from "../common/money.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { OrderScheduleItem, OrderView } from "./order.view.js";
/** Paying a schedule item by bank transfer straight to the seller (ADR 0005, PAY-10, card 055). */
export const OfflinePaymentView = z
    .object({
    id: Id,
    scheduleItemId: Id,
    amount: Money,
    paidOn: z.iso.date(),
    bankReference: z.string(),
    proofFileId: Id.describe("The remittance proof, readable by both parties"),
    status: z.enum(["submitted", "confirmed", "rejected"]),
    submittedAt: Timestamp,
    decidedAt: Timestamp.nullable(),
    rejectReason: z.string().nullable(),
})
    .meta({ id: "OfflinePayment" });
export const SchedulePayment = OrderScheduleItem.extend({
    methods: z
        .array(z.enum(["airwallex", "bank_transfer"]))
        .describe("How this item can be paid now; empty unless it is due"),
    transfers: z.array(OfflinePaymentView).describe("Transfers reported for it, newest first"),
}).meta({ id: "SchedulePayment" });
const OrderItem = z.object({ orderId: Id, itemId: Id });
const PaymentId = z.object({ paymentId: Id });
export const getOrderSchedule = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "order:read",
    id: "trade.orders.schedule",
    method: "GET",
    path: "/v1/orders/{orderId}/schedule",
    marks: ["tenant"],
    summary: "The order's schedule items with how each can be paid now and the transfers reported for it; `protection` is `none` for an order paid by bank transfer, which has no escrow",
    prd: ["PAY-3", "PAY-10"],
    request: { params: z.object({ orderId: Id }) },
    responses: {
        200: {
            description: "The schedule",
            schema: z.object({ protection: z.enum(["escrow", "none"]), items: z.array(SchedulePayment) }),
        },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const getBankTransfer = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "payment:pay",
    id: "trade.orders.schedule.bank_transfer",
    method: "GET",
    path: "/v1/orders/{orderId}/schedule/{itemId}/bank-transfer",
    marks: ["tenant"],
    summary: "For the buyer: the seller's bank details as the order keeps them, the seller's registered legal name and whether the account name is it (card 103), the amount, and the reference to quote on the transfer. `404` for an order not paid by bank transfer, whose method was off when it was placed",
    prd: ["PAY-10"],
    request: { params: OrderItem },
    responses: {
        200: {
            description: "What to transfer",
            schema: z.object({
                payee: BankDetails,
                seller: z.object({
                    legalName: z.string().describe("The seller's registered legal name"),
                    matchesAccount: z
                        .boolean()
                        .describe("Whether the account name is the legal name, read without case, punctuation, or the form of the company. When it is not, the buyer is warned to confirm the account with the seller first"),
                }),
                amount: Money,
                reference: z.string().describe("Such as `ORD-2026-000123-1`: the order number and the item"),
                dueAt: Timestamp.nullable(),
            }),
        },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const reportTransfer = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "payment:pay",
    id: "trade.orders.schedule.offline_payments.create",
    method: "POST",
    path: "/v1/orders/{orderId}/schedule/{itemId}/offline-payments",
    marks: ["tenant", "step-up", "idem"],
    summary: "The buyer reports a transfer of a due item's full amount, with the date, the bank's reference, and a clean remittance proof uploaded for the order; the seller confirms or rejects it. One report waits at a time",
    prd: ["PAY-10"],
    request: {
        params: OrderItem,
        body: z.object({
            amount: Money.describe("Exactly the item's amount"),
            paidOn: z.iso.date(),
            bankReference: z.string().trim().min(1).max(100),
            proofFileId: Id,
        }),
    },
    responses: { 201: { description: "The report", schema: OfflinePaymentView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "STEP_UP_REQUIRED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "FILE_NOT_CLEAN",
    ],
});
export const confirmTransfer = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "payment:confirm-offline",
    id: "trade.offline_payments.confirm",
    method: "POST",
    path: "/v1/offline-payments/{paymentId}/confirm",
    marks: ["tenant", "idem"],
    summary: "The seller confirms the money arrived: the item is paid without escrow, and a first payment puts the order under way",
    prd: ["PAY-10"],
    request: { params: PaymentId },
    responses: { 200: { description: "The order", schema: OrderView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const rejectTransfer = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "payment:confirm-offline",
    id: "trade.offline_payments.reject",
    method: "POST",
    path: "/v1/offline-payments/{paymentId}/reject",
    marks: ["tenant", "idem"],
    summary: "The seller did not receive it, with a reason the buyer sees; the buyer may report again",
    prd: ["PAY-10"],
    request: { params: PaymentId, body: z.object({ reason: z.string().trim().min(3).max(500) }) },
    responses: { 200: { description: "The order", schema: OrderView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
    ],
});
export const offlinePaymentRoutes = [
    getOrderSchedule,
    getBankTransfer,
    reportTransfer,
    confirmTransfer,
    rejectTransfer,
];
//# sourceMappingURL=offline-payment.routes.js.map